/**
 * @description Card moderno para vincular/desvincular conta Google
 * @module GoogleAuthCard
 * @since Novembro 2025
 * 
 * Componente visual moderno para gerenciar a integração com Google Calendar.
 * Utiliza o hook useGoogleAuth para toda a lógica de autenticação.
 */

import React, { useState } from "react";
import {
  Box,
  Button,
  Paper,
  Stack,
  Typography,
  Chip,
  CircularProgress,
  alpha,
} from "@mui/material";
import {
  Google as GoogleIcon,
  CheckCircle as CheckCircleIcon,
  LinkOff as LinkOffIcon,
  CalendarMonth as CalendarIcon,
} from "@mui/icons-material";
import { useGoogleAuth } from "@/hooks/useGoogleAuth";
import { GoogleRevokeDialog } from "@/pages/fluxextensions/components/GoogleRevokeDialog";

/**
 * Card moderno para vincular/desvincular conta Google ao Calendar
 */
export function GoogleAuthCard() {
  const { isConnected, isLoading, handleLogin, handleLogout } = useGoogleAuth();
  const [openRevokeModal, setOpenRevokeModal] = useState(false);

  // Handler para logout que abre o modal de revogação
  const handleDisconnect = async () => {
    await handleLogout();
    setOpenRevokeModal(true);
  };

  // Handler para abrir a página de permissões do Google
  const handleOpenPermissionsPage = () => {
    window.open("https://myaccount.google.com/permissions", "_blank");
  };

  return (
    <>
      <Paper
        elevation={0}
        sx={{
          p: 3,
          border: "1px solid",
          borderColor: isConnected ? alpha("#4CAF50", 0.3) : "grey.200",
          borderRadius: 2,
          background: isConnected
            ? `linear-gradient(135deg, ${alpha("#4CAF50", 0.05)} 0%, ${alpha("#2196F3", 0.05)} 100%)`
            : "transparent",
          transition: "all 0.3s ease",
        }}
      >
        <Stack spacing={2.5}>
          {/* Header com ícone e título */}
          <Stack direction="row" alignItems="center" spacing={2}>
            {/* Ícone do Google com fundo */}
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: isConnected
                  ? "linear-gradient(135deg, #4285F4 0%, #34A853 50%, #FBBC05 75%, #EA4335 100%)"
                  : "linear-gradient(135deg, #f5f5f5 0%, #e0e0e0 100%)",
                boxShadow: isConnected
                  ? "0 4px 12px rgba(66, 133, 244, 0.3)"
                  : "none",
              }}
            >
              <GoogleIcon
                sx={{
                  fontSize: 28,
                  color: isConnected ? "white" : "grey.500",
                }}
              />
            </Box>

            {/* Título e status */}
            <Box sx={{ flex: 1 }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography variant="h6" fontWeight={600}>
                  Google Agenda
                </Typography>
                <Chip
                  size="small"
                  icon={
                    isConnected ? (
                      <CheckCircleIcon sx={{ fontSize: 16 }} />
                    ) : undefined
                  }
                  label={isConnected ? "Conectado" : "Desconectado"}
                  color={isConnected ? "success" : "default"}
                  variant={isConnected ? "filled" : "outlined"}
                  sx={{
                    fontWeight: 500,
                    "& .MuiChip-icon": {
                      color: "inherit",
                    },
                  }}
                />
              </Stack>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {isConnected
                  ? "Seus agendamentos serão sincronizados automaticamente"
                  : "Vincule sua conta para sincronizar agendamentos"}
              </Typography>
            </Box>
          </Stack>

          {/* Descrição dos benefícios */}
          <Box
            sx={{
              p: 2,
              borderRadius: 1.5,
              bgcolor: alpha("#2196F3", 0.04),
              border: "1px solid",
              borderColor: alpha("#2196F3", 0.1),
            }}
          >
            <Stack direction="row" spacing={1.5} alignItems="flex-start">
              <CalendarIcon
                sx={{ fontSize: 20, color: "primary.main", mt: 0.2 }}
              />
              <Box>
                <Typography variant="body2" fontWeight={500} color="text.primary">
                  Sincronização com Google Calendar
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Seus agendamentos do Fluxmed aparecerão automaticamente no seu
                  Google Calendar, facilitando a gestão da sua agenda profissional.
                </Typography>
              </Box>
            </Stack>
          </Box>

          {/* Botão de ação */}
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            {isConnected ? (
              <Button
                variant="outlined"
                color="error"
                startIcon={
                  isLoading ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : (
                    <LinkOffIcon />
                  )
                }
                onClick={handleDisconnect}
                disabled={isLoading}
                sx={{
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 500,
                }}
              >
                {isLoading ? "Desvinculando..." : "Desvincular conta"}
              </Button>
            ) : (
              <Button
                variant="contained"
                startIcon={
                  isLoading ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : (
                    <GoogleIcon />
                  )
                }
                onClick={handleLogin}
                disabled={isLoading}
                sx={{
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 500,
                  background:
                    "linear-gradient(135deg, #4285F4 0%, #3367D6 100%)",
                  "&:hover": {
                    background:
                      "linear-gradient(135deg, #3367D6 0%, #2851A3 100%)",
                  },
                }}
              >
                {isLoading ? "Conectando..." : "Vincular conta Google"}
              </Button>
            )}
          </Box>
        </Stack>
      </Paper>

      {/* Modal para revogar permissões do Google */}
      <GoogleRevokeDialog
        isOpen={openRevokeModal}
        onClose={() => setOpenRevokeModal(false)}
        onOpenPermissionsPage={handleOpenPermissionsPage}
      />
    </>
  );
}

